import { FC, PropsWithChildren } from 'react';
import { createPortal } from 'react-dom';
import { useCoreGoalsContext } from 'modules/predictiveGoals/components/contexts/CoreGoalsContext/CoreGoalsContext';
import { ReactPortalElementId } from 'core/layouts';
import { LinkAddButton } from 'ui/linkButton';
import { LeftTooltip } from 'ui/tooltip';
import { colors } from 'ui/theme';
import { BUTTON_SIZES } from 'ui/button';
import { AIBadge } from 'ui/badge';
import {
  defaultPredictiveGoalsState,
  usePredictiveGoalsContext,
} from 'modules/predictiveGoals/components/contexts/PredictiveGoalsContext/PredictiveGoalsContext';
import { PredictiveGoalsIndexSkeleton } from 'modules/predictiveGoals/components/index/PredictiveGoalsIndexSkeleton';
import {
  isAtGoalLimit,
  isVocabularyEmpty,
  setGoalPreventionMessage,
  useGoalManagePermissionVerifier,
} from '../../goalUtils';
import {
  MAX_GOAL_LIMIT,
  PROJECT_VOCABULARY_NOT_READY_HEADER,
  NEW_GOAL,
  PREDICTIVE_GOALS,
} from '../../constants';
import {
  HeaderBadgeContainer,
  MainContentLayout,
  MainLayoutHeaderWrapper,
  MainLayoutWrapper,
  PredictiveGoalsAlignedPageHeader,
  PredictiveGoalsHeaderTitle,
  PreventNewGoalTrigger,
  StyledFullWidth,
} from '../Styled';
import { PendingAnnouncement } from '../PendingAnnouncement';
import PredictiveGoalsPage from './PredictiveGoalsPage';
import { PredictiveGoalsInitialPage } from './PredictiveGoalsInitialPage';
import { ContentUnavailable } from '../ContentUnavailable';

export const PageTestIds = {
  TemplatesDataUnavailable: 'templates-data-unavailable',
};

export interface PredictiveGoalsIndexPageProps {
  children?: React.ReactNode;
}
export const PredictiveGoalsIndexPage: FC<
  PropsWithChildren<PredictiveGoalsIndexPageProps>
> = () => {
  const {
    goals: { goalData },
    vocab: { vocabData },
    isLoading: isCoreDataLoading,
  } = useCoreGoalsContext();
  const { resetQueriedGoal, setUserTemplateSelection, isTemplateEventDisabled } =
    usePredictiveGoalsContext();
  const canManageGoals = useGoalManagePermissionVerifier();
  if (isCoreDataLoading) {
    return (
      <>
        <MainLayoutHeaderWrapper data-background-color={colors.white}>
          <MainContentLayout>
            <PredictiveGoalsAlignedPageHeader
              documentTitle="Predictive Goals"
              pageActions={
                <LinkAddButton
                  isDisabled={true}
                  to="/predictive-audiences"
                  size={BUTTON_SIZES.LARGE}
                >
                  {NEW_GOAL}
                </LinkAddButton>
              }
            >
              <HeaderBadgeContainer>
                <PredictiveGoalsHeaderTitle>{PREDICTIVE_GOALS}</PredictiveGoalsHeaderTitle>
                <AIBadge />
              </HeaderBadgeContainer>
            </PredictiveGoalsAlignedPageHeader>
          </MainContentLayout>
        </MainLayoutHeaderWrapper>
        <StyledFullWidth>
          <MainLayoutWrapper data-background-color={colors.backgroundSecondary}>
            <MainContentLayout>
              <PredictiveGoalsIndexSkeleton />
            </MainContentLayout>
          </MainLayoutWrapper>
        </StyledFullWidth>
      </>
    );
  }

  const hasGoals = goalData.length > 0;
  const isDataUnavailable = isVocabularyEmpty(vocabData);
  const portalPath = document.getElementById(ReactPortalElementId.announcements);

  const canCreateGoals = !isAtGoalLimit(MAX_GOAL_LIMIT, goalData);
  const preventNewGoalCreation = !canManageGoals || !canCreateGoals;
  const toolTipMesssage = setGoalPreventionMessage(!canManageGoals, !canCreateGoals);
  const resetGoalQueriesAndTemplates = () => {
    resetQueriedGoal();
    setUserTemplateSelection(defaultPredictiveGoalsState.userTemplateSelection);
  };

  let pageContent = null;
  if (!hasGoals) {
    if (isDataUnavailable) {
      pageContent = (
        <ContentUnavailable
          data-test={PageTestIds.TemplatesDataUnavailable}
          headerMessage={PROJECT_VOCABULARY_NOT_READY_HEADER}
          showPlaceholderContent
        >
          <PredictiveGoalsInitialPage />
        </ContentUnavailable>
      );
    } else {
      pageContent = (
        <PredictiveGoalsInitialPage
          getIsTemplateEventDisabled={isTemplateEventDisabled}
          onCreateNew={resetGoalQueriesAndTemplates}
          onSelectedEvent={setUserTemplateSelection}
        />
      );
    }
  } else {
    pageContent = <PredictiveGoalsPage />;
  }

  return (
    <>
      {isDataUnavailable && portalPath && createPortal(<PendingAnnouncement />, portalPath)}
      <MainLayoutHeaderWrapper data-background-color={colors.white}>
        <MainContentLayout>
          <PredictiveGoalsAlignedPageHeader
            documentTitle="Predictive Goals"
            pageActions={
              !hasGoals ? null : (
                <LeftTooltip isEnabled={preventNewGoalCreation} tooltipContent={toolTipMesssage}>
                  <PreventNewGoalTrigger onClick={resetGoalQueriesAndTemplates}>
                    <LinkAddButton
                      isDisabled={preventNewGoalCreation}
                      to="/predictive-audiences/goals/create"
                      size={BUTTON_SIZES.LARGE}
                    >
                      {NEW_GOAL}
                    </LinkAddButton>
                  </PreventNewGoalTrigger>
                </LeftTooltip>
              )
            }
          >
            <HeaderBadgeContainer>
              <PredictiveGoalsHeaderTitle>{PREDICTIVE_GOALS}</PredictiveGoalsHeaderTitle>
              <AIBadge />
            </HeaderBadgeContainer>
          </PredictiveGoalsAlignedPageHeader>
        </MainContentLayout>
      </MainLayoutHeaderWrapper>
      {pageContent}
    </>
  );
};
